/* eslint-disable max-lines */

import { BOOLEAN_AS_STRING } from "./Common";
import { Order } from "./ConfirmOrder";
import { NavigationScreenLinkTypes } from "./Navigation";
import { DeliveryAddress, ANNOTATION_TYPES, ANNOTATION_CATEGORY, ANNOTATION_ASSETS } from "@swiggy-private/core-types";
import { JuspayMetaDataInfo } from "./AppRedirectionInfo";
export { ANNOTATION_TYPES, ANNOTATION_CATEGORY, ANNOTATION_ASSETS };
export type { DeliveryAddress };

export enum ACTION_TYPES {
    PAY = "PAY",
    NAVIGATE = "NAVIGATE",
    DISMISS = "DISMISS",
}

interface SubscriptionOrderItem {
    name: string;
    description: string;
    planId: number;
    planDetails: {
        tenure: number;
    };
}
export interface PaymentFallbackMethod {
    name: string;
    displayName: string;
    paymentCode?: string;
    balance?: string;
    splitPayEnabled?: boolean;
}

export interface PaymentFallbacks {
    failureTitle: string;
    failureMessage: string;
    failureReason?: string;
    failureIcon: string;
    paymentMethods: PaymentFallbackMethod[];
    paymentAmountWithSplitpay?: string;
    paymentAmountWithSplitpayWithoutDiscount?: string;
    splitpay?: {
        title: string;
        select: boolean;
    };
    swiggyPayEligibleBalance?: boolean;
}

export interface PendingPaymentParams {
    host: string;
    method: string;
    endpoint: string;
    headers: { [key: string]: string };
}

export interface PostOrderTipDetails {
    amount: number;
    optIn: boolean;
    type: "POST_ORDER_TIPPING";
    cartKey: string;
    orderId: string;
}

export enum DashPaymentTypes {
    PRE_PAYMENT = "PRE_PAYMENT",
    POST_PAYMENT = "POST_PAYMENT",
    SCENES_FULL_PAYMENT = "SCENES_FULL_PAYMENT",
}

export enum DashOrderContexts {
    ORDER_JOB = "ORDER_JOB",
}

export interface Response<T = any> {
    statusCode: number;
    statusMessage?: string | null;
    data?: T | null;
    rawResponse?: Response;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

/*
 * we are including 'CANCELLED' here since we need to support the latest version of dineout pos integration,
 * which aims to support cases where POS system have cancelled a pre-book offer request for some reason.
 * This case needs to be handled gracefully at portal end since we need to pass control to diners FE for handling the status code accordingly
 * refer for more details: https://docs.google.com/document/d/1CurHW3lraQugao9-NiOX6feHM0hHAFaRYnkwpvMjbrQ/edit?pli=1#heading=h.c70if519h12k
 */

export const DEFAULT_DINEOUT_CONFIRM_ORDER_STATUS_LIST = ["CONFIRMED", "COMPLETED", "CANCELLED"];

export interface DashOrderResponse {
    orders: Array<{
        orderJobs: DashOrderJob[];
    }>;
    transactionPaymentDetails?: Array<{
        paymentTxnStatus: string;
        amazonPayResponse?: {
            amazonPayLoad: Record<string, string>;
        };
        paymentMethodMeta?: unknown;
        paymentPartnerOrderId?: string;
        paymentTxnId?: string;
    }>;
    xpData?: {
        confirmOrderEnabled?: boolean;
    };
}

export interface ConfirmDashOrderResponse {
    statusCode: number;
    statusMessage: string;
    data: DashOrderResponse | null;
}

export interface DashOrderJob {
    status: string;
    metadata: string;
    orderJobId: string;
    paymentInfo: Array<{
        paymentMethod: string;
        transactionAmount: number;
    }> | null;
}

export interface PreOrderTipDetails {
    optIn: boolean;
    type: "PRE_ORDER_TIPPING";
}

export interface OrderData {
    orderId: string | number;
    orderType: string;
    orderTotal: number;

    // This field is added for inapp upi payment method. We can remove this post
    // data localisation  and read the transaction amount from redirectional info object
    primaryPaymentTransactionAmount?: number | string;
    orderItems: Array<{
        quantity: string | number;
    }>;
    orderSubscriptions?: Array<SubscriptionOrderItem>;
    orderStatus: string;
    postStatus: string;
    orderPaymentMethod: string;
    orderTags?: string[]; //["SUPER"]
    orderTagsList?: string[]; // This is used for IRCTC orders. Eg: ["ORDER_TAG_TYPE_IRCTC"]
    paasId?: string;
    // Txn id value that can be passed to payment partner like juspay
    paymentPartnerOrderId?: string;
    paymentTxnId: string;
    paymentTxnStatus?: string;

    paymentMethodMeta?: {
        paymentRedirectUrl?: string;
        payload?: string;
        iv?: string;
        key?: string;
    };
    phonepeDebitRequisites?: {
        webRedirectionUrl?: string;
        base64Body?: string;
        checksum?: string;
        apiEndPoint?: string;
        transactionId?: string;
        s2SCallBackUrl?: string;
    };
    juspayMeta: {
        authTypeForCard?: string; // enabled only for native
        juspayClientAuthToken: string;
        juspayClientAuthTokenExpiry?: number;
        juspayMetaData?: JuspayMetaDataInfo;
        tokenRequester?: string;
        par?: string;
    };
    paymentTransactions?: Array<{
        paymentMethod: string;
        paymentMethodDisplayName: string;
        transactionId: string;
        amount: string;
        paymentGateway: string;
    }>;
    successTitle?: string;
    successMessageType: string;
    successMessage?: string;
    successMessageInfo?: string;

    // new ticks screen savings
    successMessageV2?: string;
    successMessageInfoV2?: string;

    isSwiggyBlackApplicable?: boolean;

    // for rendering otp stamp lottie on tick screen
    isSwiggyBlackOtpApplicable?: boolean;
    isSwiggyBlackOtpAnimationEnabled?: boolean;

    upiIntentUrl?: string;

    // used for swiggy money order
    upiIntentConfirmTimeInMs?: number;
    maxTimeToPollForInMs?: number;

    // contains Data with raw fields
    rawResponse: RawOrderApiData;
    pendingPaymentParams?: PendingPaymentParams | null;
    tipDetailList?: [PreOrderTipDetails, PostOrderTipDetails];

    // polling duration time for upi intent, upi collect and card - can be fraction values as well
    confirmationTimeMinutes?: string;
    // polling duration time for cred
    credConfirmationTimeMinutes?: string;
    // polling interval time for upi intent, upi collect, cred and card
    pollingIntervalInMs?: number;
    // to identify delivery speed
    selectedSlaOption?: string;
    priorityDeliveryFee?: number;
    deliveryAddress?: DeliveryAddress;

    isOfferBundleApplied?: boolean;
    // Below keys were added as part of Data Localization, and would be used for payment functionality going forth.
    // eslint-disable-next-line @typescript-eslint/naming-convention
    targetedMarketplacePaymentReferenceId?: string;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    appRedirectionInfo?: string; // this is a JSON stringified object bcz checkout teams act as passthrough so they dont touch this object. ie., they wont parse this string and send and object

    // p2p gift card related meta
    confirmationTime?: string; // to fetch the time when GC was created
    deeplink?: string; // is whatsapp sharing link for giftcard

    // xp for new confirm order api for Dineout
    newDineoutConfirmOrderEnabledXp?: boolean;

    xpData?: OrderApiXpData;

    //emi redirect url from appredirection info for EMI
    emiRedirectUrl?: string;

    //for showing new transition screen
    orderTransitionScreenNewMsgEnabled?: boolean;

    // identifier for irctc orders
    isIrctcRegularOrder?: boolean;

    successFooterMessage?: {
        text: string;
        icon?: string;
    };
    isBoltExperienceEnabledOnOrderConfirmationScreen?: boolean;
    isAffordableM2ExperienceEnabledOnOrderConfirmationScreen?: boolean;
    confirmationTimeInMinutes?: string;
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface RawOrderData {
    order_id: string | number;
    order_type: string;
    order_total: number;
    order_items: Array<{
        quantity: string | number;
    }>;
    order_status: string;
    post_status: string;
    order_payment_method: string;
    order_tags_list?: string[];
    paas_id?: string;
    payment_txn_id?: string;
    payment_txn_status?: string;
    key: string;
    payment_method_meta?: {
        payment_redirect_url?: string;
    };
    phonepe_debit_requisites?: {
        web_redirection_url?: string;
        base_64_body?: string;
        checksum?: string;
        api_end_point?: string;
        transaction_id?: string;
        s2s_call_back_url?: string;
    };
    juspay_meta: {
        auth_type_for_card?: string; // enabled only for native
        juspay_client_auth_token: string;
        juspay_client_auth_token_expiry?: number;
    };
    delivery_address?: {
        id: string;
        version: number;
        name: string;
        address_line1: string;
        address_line2: string;
        address_line_2: string;
        address: string;
        landmark: string;
        area: string;
        mobile: string;
        alternate_mobile: string;
        annotation: string;
        instructions: string;
        email: string;
        flat_no: string;
        city: string;
        lat: string;
        lng: string;
        is_verified: false;
    };
    orders?: {
        order_type: string;
        coupon_code?: string;
        order_jobs: {
            payment_info: {
                payment_method: string;
                transaction_amount: number;
            }[];
            metadata: string;
            order_job_id?: string;
        }[];
    }[];
}

export interface PaymentFailureResponse {
    failureReason?: string;
    failureReasonSubText?: string;
    autopayTimelineText?: {
        current?: string;
        next?: string;
        nextAlt?: string;
    };
    autopayCta?: {
        primary: string;
        secondary: string;
    };
}

export interface OrderApiXpData {
    showAddressVisibilityOnOrderConfirmation?: BOOLEAN_AS_STRING;
    confirmOrderEnabled?: boolean;
    orderTransitionScreenMsg?: BOOLEAN_AS_STRING;
}

/* eslint-enable @typescript-eslint/naming-convention */

export interface OrderApiData {
    paymentFailureResponse?: PaymentFailureResponse;
    orderData: OrderData | null;
    orders?: Order[];
    orderInterruptionData?: OrderInterruptionAlertData;
    invalidCouponResponse?: InvalidCouponAlertData;
    retryDelayTimeInMs: number;
    retryCount: number;
    xpData?: OrderApiXpData;
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface RawOrderApiData {
    payment_failure_response?: PaymentFailureResponse;
    order_data: RawOrderData;
    order_interruption_data?: OrderInterruptionAlertData;
    invalid_coupon_response?: InvalidCouponAlertData;
    retry_delay_time_in_ms: number;
    retry_count: number;
    xp_data?: {
        show_address_visibility_on_order_confirmation?: BOOLEAN_AS_STRING;
    };
}
/* eslint-enable @typescript-eslint/naming-convention */

interface OrderInterruptionActionBaseData {
    name?: string | null;
    displayName: string;
}
export interface PayAction extends OrderInterruptionActionBaseData {
    actionType: ACTION_TYPES.PAY;
    actionData: {
        userConsent: string;
        code: string;
        updatedAmount?: number;
    };
}

interface IScreenTypeActionData {
    screen:
        | NavigationScreenLinkTypes.CART
        | NavigationScreenLinkTypes.ORDER_DETAIL
        | NavigationScreenLinkTypes.IM_TRACK_SCREEN
        | NavigationScreenLinkTypes.TRACK
        | NavigationScreenLinkTypes.IM_CART;
    orderId?: string;
    refresh?: boolean;
    orderAmount?: string;
    orderItemsCount?: string;
    type?: undefined;
    link?: undefined;
    shouldCloseWebview?: undefined;
    isIMOrder?: boolean;
    disableDismissOfAlert?: boolean;
}

interface IDeeplinkTypeActionData {
    type: "DEEPLINK";
    link: string;
    shouldCloseWebview: boolean;
    screen?: undefined;
    orderId?: undefined;
    refresh?: undefined;
    orderAmount?: undefined;
    orderItemsCount?: undefined;
    disableDismissOfAlert?: boolean;
}

export interface NavigateAction extends OrderInterruptionActionBaseData {
    actionType: ACTION_TYPES.NAVIGATE;
    actionData: IScreenTypeActionData | IDeeplinkTypeActionData;
}

export interface DismissAction extends OrderInterruptionActionBaseData {
    actionType: ACTION_TYPES.DISMISS;
    actionData?: {
        orderAmount?: string;
        orderItemsCount?: string;
    } | null;
}

export type OrderInterruptionAction = NavigateAction | PayAction | DismissAction;

export interface OrderInterruptionAlertData {
    title: string;
    subtitle: string;
    message: string | null;
    icon: string | null;
    actions: OrderInterruptionAction[];
}

export interface InvalidCouponAlertData {
    couponErrorMessage: string;
}

export interface InvalidCouponAction {
    displayName: string;
    actionType: string;
}

export interface DirectPlaceOrderPayload {
    fromCart: boolean;
    cartTotal: number;
    orderComment: string;
}

export interface IMetaData {
    dealCategory: string; // can added extra fields from the metadata in this interface if required
    type: string;
    successInfo?: {
        message?: string;
        savingText?: string;
    };
}

export interface OrderIdMismatchAlertPayload {
    title?: string;
    subtitle?: string;
    icon?: string;
    cta?: {
        text?: string;
        onClick?: VoidFunction;
    };
}
